import { getSecondsInTimeInterval } from 'client/modules/Date';
import { TimeInterval } from 'client/modules/Date/types';
import { getApiUrl } from 'client/modules/Url';

export type FetchStationPlaysByDateRangeProps = {
  webStationId: string;
  timeInterval: TimeInterval;
  range?: {
    timeRangeStart: number;
    timeRangeEnd: number;
  };
};

export type FetchStationPlaysByDateRangeResponse = {
  data: {
    columnHeaders: { name: string; type: string }[];
    rows: [number, number][]; // [dateTimestamp, numOfPlays]
  };
  kind: 'plays';
  parameters: {
    timeInterval: number;
    timeRange: [number, number];
  };
  stationId: number;
};

export async function fetchStationPlaysByDateRange({
  webStationId,
  timeInterval = 'hour',
  range,
}: FetchStationPlaysByDateRangeProps): Promise<FetchStationPlaysByDateRangeResponse> {
  try {
    const resp = await fetch(
      getStationPlaysUrl({
        webStationId,
        timeInterval,
        range,
      }),
      {
        method: 'GET',
        credentials: 'same-origin',
      }
    );

    if (resp.ok) {
      return resp.json();
    }

    throw new Error('Could not fetch station plays by date range');
  } catch (err) {
    throw new Error(err.message);
  }
}

export function getStationPlaysUrl({
  webStationId,
  timeInterval,
  range,
  csvFilename,
}: Omit<
  FetchStationPlaysByDateRangeProps,
  'topEpisodesCountToInclude' | 'shouldIncludePublishedEpisodes'
> & {
  csvFilename?: string;
}) {
  const intervalInSecs = getSecondsInTimeInterval(timeInterval);
  const queryParameters = {
    timeInterval: intervalInSecs,
    ...(range && {
      timeRangeStart: range.timeRangeStart,
      timeRangeEnd: range.timeRangeEnd,
    }),
    ...(csvFilename && { csvFilename }),
  };

  return getApiUrl({
    path: `analytics/station/webStationId:${webStationId}/plays`,
    queryParameters,
  });
}
